import color from "../Constants/colors";
import {
  ProcessingStatus,
  KnowledgeBaseStatus,
  WorkspaceTerminology,
  ModelLevel,
  LoginMethod,
  Languages,
} from "../Enums/companyStatus";

export const truncateString = (str: string, maxLength: number): string => {
  if (str.length <= maxLength) return str;
  return `${str.substring(0, maxLength - 3)}...`;
};

export const truncateText = (text: string, maxLength: number = 24): string => {
  return text.length > maxLength
    ? text.substring(0, maxLength - 2) + "..."
    : text;
};

export const getSubstringAfterLastPeriod = (input: string): string => {
  const lastIndex = input.lastIndexOf(".");
  return lastIndex === -1 ? "" : input.substring(lastIndex + 1);
};

const errors = {
  error: "Error happened while processing File",
  timeError: "Error: time limit exceeded",
};

export const statusToStringFile = (status: number): string => {
  switch (status) {
    case ProcessingStatus.WAITING:
      return "Waiting for processing";
    case ProcessingStatus.IN_PROGRESS:
      return "File is being processed";
    case ProcessingStatus.PROCESSED:
      return "File is processed";
    case ProcessingStatus.ERROR:
      return errors.error;
    case ProcessingStatus.TIME_LIMIT_EXCEEDED:
      return errors.timeError;
    case ProcessingStatus.STOPPED:
      return "Processing stopped";
    default:
      return "Unknown Status";
  }
};

export const isStatusToStringFileError = (status: number): boolean => {
  const statusMessage = statusToStringFile(status);
  return statusMessage === errors.error || statusMessage === errors.timeError;
};

export const statusToStringKnowledgeBase = (status: number): string => {
  switch (status) {
    case KnowledgeBaseStatus.WAITING:
      return "Waiting";
    case KnowledgeBaseStatus.IN_PROGRESS:
      return "Indexing";
    case KnowledgeBaseStatus.PROCESSED:
      return "Completed";
    case KnowledgeBaseStatus.ERROR:
      return "Error";
    case KnowledgeBaseStatus.TIME_LIMIT_EXCEEDED:
      return "Time limit exceeded";
    case KnowledgeBaseStatus.STOPPED:
      return "Canceled";
    default:
      return "Unknown";
  }
};

export const statusToStringWebsites = (status: number): string => {
  switch (status) {
    case ProcessingStatus.WAITING:
      return "Waiting for processing";
    case ProcessingStatus.IN_PROGRESS:
      return "Website is being processed";
    case ProcessingStatus.PROCESSED:
      return "Website is processed";
    case ProcessingStatus.ERROR:
      return "Error happened while processing website";
    case ProcessingStatus.TIME_LIMIT_EXCEEDED:
      return "Error: time limit exceeded";
    case ProcessingStatus.STOPPED:
      return "Website is stopped";
    default:
      return "Unknown Status";
  }
};

export const translateFieldName = (
  key: string,
  t: (key: string) => string,
): string => {
  const translationKey = `documentUpload.coverSheetFields.${key}`;
  const translated = t(translationKey);
  return translated !== translationKey ? translated : key;
};

export const terminologyToString = (value: WorkspaceTerminology): string => {
  switch (value) {
    case WorkspaceTerminology.DEFAULT:
      return "DEFAULT";
    case WorkspaceTerminology.POWERPLANT:
      return "POWERPLANT";
    case WorkspaceTerminology.COMPANY:
      return "COMPANY";
    case WorkspaceTerminology.PRODUCT:
      return "PRODUCT";
    case WorkspaceTerminology.DEVICE:
      return "DEVICE";
    case WorkspaceTerminology.PROJECT:
      return "PROJECT";
    default:
      return "UNKNOWN";
  }
};

export const modelLevelToString = (level: ModelLevel) => {
  switch (level) {
    case ModelLevel.BASIC:
      return "BASIC";
    case ModelLevel.ENTRY:
      return "ENTRY";
    case ModelLevel.FULL:
      return "FULL";
    default:
      return "Unknown";
  }
};

export const loginMethodToString = (method: LoginMethod) => {
  switch (method) {
    case LoginMethod.PASSWORD:
      return "Password";
    case LoginMethod.ENTRA_ID:
      return "Entra ID";
    default:
      return "Unknown";
  }
};

export const shortTaskName = (fullName: string): string => {
  const parts = fullName.split(".");
  return parts[parts.length - 1] || fullName;
};

export const formatUTC = (utc: string) =>
  `UTC${parseInt(utc, 10)}${utc.endsWith(":00") ? "" : utc.slice(3)}`;

export const getLevelColor = (level: string) => {
  switch (level.toUpperCase()) {
    case "INFO":
      return { bg: color.blue50, text: color.blue700, border: color.blue200 };
    case "WARNING":
      return {
        bg: color.yellow50,
        text: color.yellow700,
        border: color.yellow200,
      };
    case "ERROR":
      return { bg: color.red50, text: color.red600, border: color.red200 };
    default:
      return { bg: color.gray50, text: color.gray600, border: color.gray200 };
  }
};

export const getMessageStyle = (message: string) => {
  if (message.includes("✅")) return { color: color.green700, fontWeight: 600 };
  if (message.includes("❌")) return { color: color.red600, fontWeight: 600 };
  if (message.includes("🪟")) return { color: color.blue700, fontWeight: 600 };
  if (message.includes("🔁"))
    return { color: color.orange500, fontWeight: 600 };
  return { color: color.textPrimary, fontWeight: 400 };
};

export const getLanguageTranslationKey = (id: number): string => {
  switch (id) {
    case Languages.ENGLISH:
      return "evaluationSelector.inEnglish";
    case Languages.GERMAN:
      return "evaluationSelector.inGerman";
    case Languages.SERBIAN:
      return "evaluationSelector.inSerbian";
    default:
      return "evaluationSelector.inUnknown";
  }
};

// Mirrors the backend model_map. An id keeps its tier when the model behind
// it is upgraded; ids 1 and 3 are retired and only appear in history.
export const getModelName = (modelId?: number) => {
  const models = [
    { model_id: 0, model_name: "gpt-6.1-sol" },
    { model_id: 1, model_name: "gpt-5.4" },
    { model_id: 2, model_name: "gpt-6-luna" },
    { model_id: 3, model_name: "gpt-5.4-nano" },
    { model_id: 4, model_name: "claude-opus-5-5" },
    { model_id: 5, model_name: "claude-sonnet-5-5" },
    { model_id: 6, model_name: "claude-haiku-5-5" },
    { model_id: 7, model_name: "mistral-large-latest" },
    { model_id: 8, model_name: "mistral-medium-latest" },
    { model_id: 9, model_name: "mistral-small-latest" },
    { model_id: 10, model_name: "comma-soft/gemma4-31b-instant" },
    { model_id: 11, model_name: "comma-soft/gemma4-31b" },
  ];
  const model = models.find((m) => m.model_id === modelId);
  return model?.model_name ?? "-";
};
